/**
 * Module điều khiển bảng tính (Spreadsheet Grid Engine)
 * Hỗ trợ chế độ xuống dòng tự động (Wrap Text) cho các ô tính:
 * - Khi văn bản dài vượt quá chiều rộng của ô, nội dung tự động xuống dòng.
 * - Chiều cao của hàng (.grid-row) tự động co giãn theo nội dung.
 * - Chiều cao của số thứ tự hàng (.row-header) được đồng bộ chính xác từng pixel với hàng đó.
 */
import { SPREADSHEET_CONFIG } from '../config/constants.js';

export class SpreadsheetModule {
  constructor() {
    this.cellData = new Map(); // key dạng "row:col"
    this.selectedCell = { row: 1, col: 'A' };
    this.totalRows = SPREADSHEET_CONFIG.defaultRows;

    // Chế độ xuống dòng tự động (Mặc định BẬT để đọc truyện trọn vẹn)
    this.isWrapText = true;

    // Cache DOM Elements
    this.gridCells = document.getElementById('grid-cells');
    this.colHeaders = document.getElementById('col-headers');
    this.rowHeaders = document.getElementById('row-headers');
    this.nameBox = document.getElementById('name-box');
    this.formulaInput = document.getElementById('formula-input');
    this.btnWrapText = document.getElementById('btn-wrap-text');

    // Bật class wrap-text ngay từ đầu
    if (this.gridCells && this.isWrapText) {
      this.gridCells.classList.add('wrap-text');
    }

    this.bindEvents();
  }

  bindEvents() {
    // 1. Đồng bộ cuộn giữa Grid và Tiêu đề Cột / Tiêu đề Hàng
    this.gridCells.addEventListener('scroll', () => {
      this.colHeaders.scrollLeft = this.gridCells.scrollLeft;
      this.rowHeaders.scrollTop = this.gridCells.scrollTop;
    });

    // 2. Lắng nghe sự thay đổi trên thanh công thức fx
    this.formulaInput.addEventListener('input', () => {
      this.setCellValue(this.selectedCell.row, this.selectedCell.col, this.formulaInput.value);
      const target = document.querySelector(
        `[data-row="${this.selectedCell.row}"][data-col="${this.selectedCell.col}"]`
      );
      if (target) {
        target.textContent = this.formulaInput.value;
        // Cập nhật lại chiều cao hàng nếu text thay đổi độ dài
        this.syncRowHeights();
      }
    });

    // 3. Nút bật / tắt tính năng Xuống dòng tự động (Wrap Text) trên Toolbar
    if (this.btnWrapText) {
      this.btnWrapText.addEventListener('click', () => {
        this.toggleWrapText();
      });
    }

    // 4. Lắng nghe thay đổi kích thước cửa sổ để tính toán lại chiều cao hàng
    window.addEventListener('resize', () => {
      if (this.isWrapText) {
        this.syncRowHeights();
      }
    });

    // 5. Phím mũi tên Lên (↑) / Xuống (↓) để duyệt từng câu truyện trên thanh fx
    window.addEventListener('keydown', (e) => {
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextRow = Math.min(this.totalRows, this.selectedCell.row + 1);
        this.selectCell(nextRow, this.selectedCell.col);
        this.scrollCellIntoView(nextRow);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevRow = Math.max(1, this.selectedCell.row - 1);
        this.selectCell(prevRow, this.selectedCell.col);
        this.scrollCellIntoView(prevRow);
      }
    });
  }

  /**
   * Chuyển đổi trạng thái Xuống dòng tự động (Wrap Text)
   */
  toggleWrapText() {
    this.isWrapText = !this.isWrapText;

    if (this.gridCells) {
      this.gridCells.classList.toggle('wrap-text', this.isWrapText);
    }
    if (this.btnWrapText) {
      this.btnWrapText.classList.toggle('active', this.isWrapText);
    }

    // Đồng bộ lại toàn bộ chiều cao hàng
    this.syncRowHeights();
  }

  /**
   * Đồng bộ chiều cao hàng thực tế với các số thứ tự hàng bên trái (.row-header)
   * Giúp bảng tính không bao giờ bị lệch hàng khi văn bản xuống nhiều dòng
   */
  syncRowHeights() {
    if (!this.gridCells || !this.rowHeaders) return;

    requestAnimationFrame(() => {
      const gridRows = this.gridCells.querySelectorAll('.grid-row');
      const rowHeaders = this.rowHeaders.querySelectorAll('.row-header');
      const total = Math.min(gridRows.length, rowHeaders.length);

      for (let i = 0; i < total; i++) {
        const rowEl = gridRows[i];
        const headerEl = rowHeaders[i];

        if (this.isWrapText) {
          // Lấy chiều cao thực tế của hàng (đã tự co giãn theo nội dung câu chuyện)
          const rowHeight = rowEl.offsetHeight;
          if (rowHeight > 0) {
            headerEl.style.height = `${rowHeight}px`;
          }
        } else {
          // Khi tắt wrap text, trả về chiều cao mặc định 24px
          headerEl.style.height = '24px';
        }
      }
    });
  }

  scrollCellIntoView(row) {
    const target = document.querySelector(`[data-row="${row}"][data-col="${this.selectedCell.col}"]`);
    if (target) {
      target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  getKey(row, col) {
    return `${row}:${col}`;
  }

  setCellValue(row, col, value) {
    this.cellData.set(this.getKey(row, col), value);
  }

  getCellValue(row, col) {
    return this.cellData.get(this.getKey(row, col)) ?? '';
  }

  clear() {
    this.cellData.clear();
  }

  renderColumnHeaders() {
    this.colHeaders.innerHTML = '';
    SPREADSHEET_CONFIG.columns.forEach(c => {
      const el = document.createElement('div');
      el.className = 'col-header';
      el.style.width = `${c.width}px`;
      el.textContent = c.key;
      if (c.key === this.selectedCell.col) el.classList.add('selected');
      this.colHeaders.appendChild(el);
    });
  }

  renderRowHeaders() {
    this.rowHeaders.innerHTML = '';
    for (let r = 1; r <= this.totalRows; r++) {
      const el = document.createElement('div');
      el.className = 'row-header';
      el.textContent = r;
      if (r === this.selectedCell.row) el.classList.add('selected');
      this.rowHeaders.appendChild(el);
    }
  }

  renderGrid() {
    this.gridCells.innerHTML = '';

    for (let r = 1; r <= this.totalRows; r++) {
      const rowEl = document.createElement('div');
      rowEl.className = 'grid-row';
      rowEl.dataset.row = r;

      SPREADSHEET_CONFIG.columns.forEach(col => {
        const cellEl = document.createElement('div');
        cellEl.className = `grid-cell col-${col.key}`;
        cellEl.style.width = `${col.width}px`;
        cellEl.dataset.row = r;
        cellEl.dataset.col = col.key;
        cellEl.textContent = this.getCellValue(r, col.key);

        if (r === this.selectedCell.row && col.key === this.selectedCell.col) {
          cellEl.classList.add('selected');
        }

        cellEl.addEventListener('click', () => {
          this.selectCell(r, col.key);
        });

        rowEl.appendChild(cellEl);
      });

      this.gridCells.appendChild(rowEl);
    }

    // Đồng bộ chiều cao hàng ngay sau khi render xong toàn bộ lưới ô
    this.syncRowHeights();
  }

  selectCell(row, col) {
    this.selectedCell = { row, col };
    this.nameBox.textContent = `${col}${row}`;
    this.formulaInput.value = this.getCellValue(row, col);

    // Cập nhật trạng thái đổi màu Header Cột & Header Hàng
    document.querySelectorAll('.col-header').forEach((h, idx) => {
      h.classList.toggle('selected', SPREADSHEET_CONFIG.columns[idx].key === col);
    });
    document.querySelectorAll('.row-header').forEach((h, idx) => {
      h.classList.toggle('selected', (idx + 1) === row);
    });
    document.querySelectorAll('.grid-cell').forEach(c => {
      const isTarget = parseInt(c.dataset.row) === row && c.dataset.col === col;
      c.classList.toggle('selected', isTarget);
    });
  }
}